import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Target, Activity, Stethoscope } from "lucide-react";
import { api } from "@/lib/api";
import { CONDITIONS } from "@/lib/conditions";

export default function ConditionPage() {
  const { slug } = useParams();
  const c = CONDITIONS[slug];
  const [products, setProducts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!c) return;
    // Set SEO metadata
    document.title = c.seo_title;
    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", name);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };
    setMeta("description", c.seo_desc);
    api.get("/products?limit=100").then(({ data }) => setProducts(data.items || data || [])).catch(() => {});
  }, [c]);

  if (!c) return <Navigate to="/shop" replace />;

  const primary = products.find((p) => p.slug === c.primary_product_slug);
  const supporting = products.find((p) => p.slug === c.supporting_product_slug);

  return (
    <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid={`condition-${slug}`}>
      {/* HERO */}
      <section className="section-dark relative overflow-hidden py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(52,216,160,0.12),transparent_55%)]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
          {/* Breadcrumbs */}
          <nav className="mb-6 font-mono text-[11px] uppercase tracking-overline text-white/40" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/shop" className="hover:text-white">Recover By Condition</Link>
            <span className="mx-2">/</span>
            <span className="text-white/70">{c.name}</span>
          </nav>
          <p className="font-mono text-xs uppercase tracking-overline text-[#34D8A0]">{c.tag}</p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-4 max-w-3xl font-display text-5xl font-black leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
          >
            {c.name}.
          </motion.h1>
          <p className="mt-6 max-w-xl text-lg font-medium leading-relaxed text-white/85">{c.hero}</p>
        </div>
      </section>

      {/* UNDERSTANDING */}
      <section className="border-b border-black/10 bg-white px-5 py-20 sm:px-8 sm:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-3">
          <div>
            <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Understanding</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">The condition, in context.</h2>
          </div>
          <p className="text-base leading-relaxed text-black/70 lg:col-span-2">{c.understanding}</p>
        </div>
      </section>

      {/* GOALS + WHERE NUTRITION FITS */}
      <section className="bg-[#F9F9F6] px-5 py-20 sm:px-8 sm:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-2">
          <div className="rounded-2xl border border-black/10 bg-white p-8">
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">
              <Target className="h-3.5 w-3.5" /> Common Rehabilitation Goals
            </div>
            <ul className="mt-5 space-y-3">
              {c.goals.map((g) => (
                <li key={g} className="flex items-center gap-3 text-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#34D8A0]" />
                  <span className="font-medium text-[#0A2540]">{g}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-black/10 bg-white p-8">
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-overline text-[#2F80ED]">
              <Activity className="h-3.5 w-3.5" /> Where Nutrition Fits
            </div>
            <p className="mt-5 text-base leading-relaxed text-black/70">{c.where_nutrition_fits}</p>
          </div>
        </div>
      </section>

      {/* RECOVERY PATHWAY */}
      <section className="border-y border-black/10 bg-[#0A2540] px-5 py-20 text-white sm:px-8 sm:py-24">
        <div className="mx-auto max-w-[1400px]">
          <p className="font-mono text-xs uppercase tracking-overline text-[#34D8A0]">Recovery Pathway</p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
            Assess. Rehabilitate. Recover.
          </h2>
          <div className="mt-12 flex flex-wrap items-center gap-3">
            {c.pathway.map((step, i) => (
              <React.Fragment key={step}>
                <div className="rounded-full border border-[#34D8A0]/40 bg-white/5 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-overline">
                  <span className="mr-2 text-[#34D8A0]">{String(i + 1).padStart(2, "0")}</span>
                  {step}
                </div>
                {i < c.pathway.length - 1 && <ArrowRight className="h-4 w-4 text-white/30" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* RECOMMENDED FORMULA */}
      <section className="bg-white px-5 py-24 sm:px-8">
        <div className="mx-auto max-w-[1400px]">
          <p className="font-mono text-xs uppercase tracking-overline text-[#2F80ED]">Recommended Category · {c.primary_category}</p>
          <h2 className="mt-3 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
            The nutritional support that fits.
          </h2>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {primary && (
              <Link to={`/product/${primary.slug}`} className="group flex flex-col gap-4 border-2 border-[#0A2540] bg-white p-6 transition-colors hover:bg-[#0A2540] hover:text-white sm:flex-row sm:gap-6 sm:p-8" data-testid="primary-product">
                {primary.image && <img src={primary.image} alt={primary.name} className="h-24 w-24 shrink-0 object-contain sm:h-32 sm:w-32" />}
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-overline text-[#2F80ED] group-hover:text-[#34D8A0]">Primary Formula</div>
                  <div className="mt-2 font-display text-xl font-bold tracking-tight sm:text-2xl">{primary.name}</div>
                  <p className="mt-2 text-sm leading-relaxed opacity-70">{primary.short_description || primary.description?.slice(0, 140)}</p>
                  <div className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline">
                    View Formula <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            )}
            {supporting && (
              <Link to={`/product/${supporting.slug}`} className="group flex flex-col gap-4 border border-black/15 bg-white p-6 transition-colors hover:border-[#2F80ED] sm:flex-row sm:gap-6 sm:p-8" data-testid="supporting-product">
                {supporting.image && <img src={supporting.image} alt={supporting.name} className="h-24 w-24 shrink-0 object-contain sm:h-32 sm:w-32" />}
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-overline text-black/50">Supporting Formula</div>
                  <div className="mt-2 font-display text-xl font-bold tracking-tight text-[#0A2540] sm:text-2xl">{supporting.name}</div>
                  <p className="mt-2 text-sm leading-relaxed text-black/60">{supporting.short_description || supporting.description?.slice(0, 140)}</p>
                  <div className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-[#2F80ED]">
                    View Formula <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            )}
          </div>

          {/* Physiotherapist note */}
          <div className="mt-14 flex gap-4 rounded-2xl border border-[#2F80ED]/30 bg-[#2F80ED]/[0.04] p-6">
            <Stethoscope className="mt-1 h-6 w-6 shrink-0 text-[#2F80ED]" />
            <div>
              <div className="font-display text-lg font-bold tracking-tight text-[#0A2540]">Your physiotherapy programme remains central to functional recovery.</div>
              <p className="mt-2 text-sm leading-relaxed text-black/60">Nutritional support is complementary. Always follow your clinician's prescribed rehabilitation plan.</p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-14 flex flex-wrap gap-3">
            <Link to={`/product/${c.primary_product_slug}`} className="inline-flex items-center gap-2 rounded-full bg-[#34D8A0] px-8 py-4 font-semibold text-black transition-transform hover:scale-[1.03]">
              Explore {c.primary_category} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/find-my-formula" className="inline-flex items-center gap-2 rounded-full border border-black/20 px-8 py-4 font-semibold text-[#0A2540] hover:bg-[#0A2540] hover:text-white">
              Find My Recovery Formula
            </Link>
          </div>

          {/* Disclaimer */}
          <div className="mt-10 flex items-start gap-3 rounded-md border border-black/10 bg-black/[0.02] p-4 text-xs leading-relaxed text-black/60">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-black/40" />
            <span>
              Physio Essential products are nutritional supplements intended to complement a balanced diet and healthy lifestyle. They are not intended to diagnose, treat, cure or prevent disease. Consult a qualified healthcare professional for condition-specific advice.
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
